/**
 * Program Categories sagas
 */
import { call, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import { httpRequest } from '../../services/http-service';
import { endPoints } from '../../services/endpoints';
import {
  fetchProgramCategories,
  fetchProgramCategory,
  createProgramCategory,
  updateProgramCategoryAction,
  deleteProgramCategory,
  setCategories,
  setCurrentCategory,
  addCategory,
  updateCategory,
  removeCategory,
  setLoading,
  setError,
} from './programCategoriesSlice';
import { presentLoader, dismissLoader } from '../../store/slices/commonSlice';

/**
 * Fetch all program categories saga
 */
function* fetchProgramCategoriesSaga(): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.programCategories.getAll);

    if (response?.success) {
      yield put(setCategories(response.data || response));
    } else {
      toast.error('Failed to fetch program categories');
      yield put(setError('Failed to fetch program categories'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch program categories';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single program category saga
 */
function* fetchProgramCategorySaga(action: ReturnType<typeof fetchProgramCategory>): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(httpRequest.get, endPoints.programCategories.getById(action.payload));

    if (response?.success) {
      yield put(setCurrentCategory(response.data || response));
    } else {
      toast.error('Failed to fetch program category');
      yield put(setError('Failed to fetch program category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to fetch program category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create program category saga
 */
function* createProgramCategorySaga(action: ReturnType<typeof createProgramCategory>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.post, endPoints.programCategories.create, action.payload);

    if (response?.success) {
      const newCategory = response.data || response;
      yield put(addCategory(newCategory));
    } else {
      toast.error('Failed to create program category');
      yield put(setError('Failed to create program category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to create program category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update program category saga
 */
function* updateProgramCategorySaga(action: ReturnType<typeof updateProgramCategoryAction>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.programCategories.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedCategory = { ...response.data, _id: action.payload.id };
      yield put(updateCategory(updatedCategory));
    } else {
      toast.error('Failed to update program category');
      yield put(setError('Failed to update program category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to update program category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete program category saga
 */
function* deleteProgramCategorySaga(action: ReturnType<typeof deleteProgramCategory>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(httpRequest.delete, endPoints.programCategories.delete(action.payload));

    if (response?.success || response) {
      yield put(removeCategory(action.payload));
    } else {
      toast.error('Failed to delete program category');
      yield put(setError('Failed to delete program category'));
    }
  } catch (error: any) {
    const message = error?.message || 'Failed to delete program category';
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* programCategoriesSagas(): Generator<any, void, any> {
  yield takeLatest(fetchProgramCategories, fetchProgramCategoriesSaga);
  yield takeLatest(fetchProgramCategory, fetchProgramCategorySaga);
  yield takeLatest(createProgramCategory, createProgramCategorySaga);
  yield takeLatest(updateProgramCategoryAction, updateProgramCategorySaga);
  yield takeLatest(deleteProgramCategory, deleteProgramCategorySaga);
}

